import React from 'react';
import { GalleryHorizontalEnd, Maximize, Minimize, Pause, Pencil, Play, RotateCcw, SkipBack, SkipForward } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SegmentedTimeline } from './SegmentedTimeline';
import { formatTime } from '@/lib/studio/format';
import { IconTipB } from '@/components/studio/IconTipB';
import type { StudioPlayerControlsProps } from './StudioPlayerControls';

function RowIcon({ label, hint = label, onClick, disabled, pressed, className, children }: { label: string; hint?: string; onClick: () => void; disabled?: boolean; pressed?: boolean; className?: string; children: React.ReactNode }) {
  return (
    <IconTipB label={hint} side="top">
      <button
        type="button"
        aria-label={label}
        aria-pressed={pressed}
        onClick={onClick}
        disabled={disabled}
        className={cn(
          'grid h-8 w-8 flex-none place-items-center rounded-[var(--b-radius-control)] transition-colors disabled:opacity-30',
          pressed ? 'bg-b-raised text-b-t1' : 'text-b-t2 hover:bg-b-raised hover:text-b-t1',
          className,
        )}
      >
        {children}
      </button>
    </IconTipB>
  );
}

/** A control that is on its way: a disabled button can't take hover or focus, so this holds them and says why. */
export function ComingSoon({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className={cn('inline-flex flex-none cursor-not-allowed rounded-[var(--b-radius-control)]', className)}>{children}</span>
        </TooltipTrigger>
        <TooltipContent>Coming soon</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** The row over the video in full screen: white on a dark fade, whatever the theme. */
const OVERLAY_TOKENS = {
  '--b-text-1': '0 0% 100%',
  '--b-text-2': '0 0% 100%',
  '--b-raised': '0 0% 100% / 0.16',
  '--b-action': '0 0% 100%',
  '--b-on-action': '262 29% 10%',
} as React.CSSProperties;

/** Direction B's controls (beta only): one 44 px row under the stage. Play takes the action ink; the progress keeps the violet. */
export const StudioPlayerControlsB: React.FC<StudioPlayerControlsProps> = ({
  isVideoMode,
  slideCount,
  currentIndex,
  isPlaying,
  currentTime,
  duration,
  progress,
  segmented,
  onTimelineSeek,
  onTimelineSlideClick,
  onSeek,
  onRestart,
  onPrev,
  onNext,
  onPlayPause,
  isEditMode,
  onEnterEdit,
  onFullscreen,
  showFilmstrip,
  onToggleFilmstrip,
  fullscreen = false,
  phone = false,
  scene,
  playable = true,
}) => {
  const several = !phone && isVideoMode && slideCount > 1;
  const play = (
    <button
      type="button"
      aria-label={isPlaying ? 'Pause' : 'Play'}
      title={playable ? undefined : 'This scene is still being built'}
      onClick={onPlayPause}
      disabled={!playable}
      className="grid h-8 w-8 flex-none place-items-center rounded-full bg-b-action text-b-on-action transition-transform active:scale-95 disabled:opacity-30 disabled:active:scale-100"
    >
      {isPlaying ? <Pause className="h-3.5 w-3.5 fill-current" /> : <Play className="ml-0.5 h-3.5 w-3.5 fill-current" />}
    </button>
  );
  return (
    <div
      data-controls="b"
      data-overlay={fullscreen || undefined}
      style={fullscreen ? OVERLAY_TOKENS : undefined}
      className={cn(
        'flex h-[var(--b-controls)] items-center gap-3',
        fullscreen ? 'bg-gradient-to-t from-black/80 to-transparent' : 'bg-b-chrome',
        'pl-4 pr-3',
        !fullscreen && 'shadow-[inset_0_1px_0_hsl(var(--b-line))]',
      )}
    >
      {playable ? <IconTipB label={isPlaying ? 'Pause' : 'Play'} side="top">{play}</IconTipB> : play}
      {!fullscreen && !phone && <RowIcon label="Restart" onClick={onRestart} className="md:max-lg:hidden"><RotateCcw className="h-4 w-4" /></RowIcon>}
      {several && <RowIcon label="Previous scene" onClick={onPrev} disabled={currentIndex <= 0}><SkipBack className="h-4 w-4" /></RowIcon>}
      {several && <RowIcon label="Next scene" onClick={onNext} disabled={currentIndex >= slideCount - 1}><SkipForward className="h-4 w-4" /></RowIcon>}
      <span className="flex-none font-mono text-b-time tabular-nums text-b-t2">{formatTime(currentTime)} / {formatTime(duration)}</span>

      <div className={cn(phone ? 'min-w-0' : 'min-w-20', 'flex-1')}>
        {scene && showFilmstrip ? (
          <div data-scene-title className="flex min-w-0 items-baseline justify-center gap-2">
            <span className="truncate text-[13px] font-medium text-b-t1">{scene.title}</span>
            {scene.total > 1 && <span className="flex-none text-[12px] tabular-nums text-b-t3">{scene.number} of {scene.total}</span>}
          </div>
        ) : segmented ? (
          <SegmentedTimeline
            manifest={segmented.manifest}
            playbackState={segmented.playbackState}
            onSeek={onTimelineSeek}
            onSlideClick={onTimelineSlideClick}
            interactive={true}
            showTransitions={false}
          />
        ) : (
          <div
            role="slider"
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={Math.round(duration)}
            aria-valuenow={Math.round(currentTime)}
            tabIndex={0}
            className="flex h-6 cursor-pointer items-center"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              onSeek(((e.clientX - rect.left) / rect.width) * duration);
            }}
          >
            <div className="h-1 w-full overflow-hidden rounded-full bg-b-line-strong">
              <div className="h-full rounded-full bg-primary" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}
      </div>

      {!isEditMode && !fullscreen && (
        <ComingSoon className="md:max-lg:hidden">
          <button
            type="button"
            aria-label={phone ? 'Edit' : undefined}
            disabled
            onClick={onEnterEdit}
            className="pointer-events-none flex h-7 flex-none items-center gap-1.5 rounded-[var(--b-radius-control)] px-2 text-b-meta text-b-t2 opacity-50"
          >
            <Pencil className="h-3.5 w-3.5" />
            {!phone && 'Edit'}
          </button>
        </ComingSoon>
      )}
      {fullscreen ? (
        <RowIcon label="Exit full screen" hint="Exit full screen (F)" onClick={onFullscreen}><Minimize className="h-4 w-4" /></RowIcon>
      ) : (
        <RowIcon label="Full screen" hint="Full screen (F)" onClick={onFullscreen}><Maximize className="h-4 w-4" /></RowIcon>
      )}
      {isVideoMode && !fullscreen && !phone && (
        <RowIcon label={showFilmstrip ? 'Hide scenes' : 'Show scenes'} pressed={showFilmstrip} onClick={onToggleFilmstrip}>
          <GalleryHorizontalEnd className="h-4 w-4" />
        </RowIcon>
      )}
    </div>
  );
};
